// Single source of truth for transaction view model computation
// All displayed numbers must come from this module

export interface LedgerRow {
  id: string
  posting: string
  attemptId: string | null
  type:
    | 'payment'
    | 'fee'
    | 'refund'
    | 'dispute'
    | 'reserve_hold'
    | 'reserve_release'
    | 'dispute_reversal'
  gross: number | null
  feeExTax: number | null
  taxAmount: number | null
  status: 'Posted' | 'Pending' | 'Scheduled'
  effectiveAt: string
  settlement: {
    kind: 'payout' | 'reserve'
    id: string
    date?: string
    releaseDate?: string
    status: string
    amountInBucket: number
  } | null
  feeBreakdown: {
    jurisdiction: string
    items: Array<{
      label: string
      amountExTax: number
      taxAmount: number
    }>
  } | null
}

export interface Transaction {
  id: string
  transactionId: string
  amount: number
  currency: string
  date: string
}

export interface PayoutBucket {
  id: string
  date: string
  status: string
  total: number
  rows: string[]
}

export interface ReserveBucket {
  id: string
  heldNow: number
  releaseAt: string | null
}

export interface TimelineItem {
  at: string
  label: string
  amount: number | null
  type: 'positive' | 'negative' | 'neutral'
}

export interface TransactionViewModel {
  // Header
  headerAmount: number
  headerState: string

  // Financial summary
  summary: {
    grossImpact: number
    feesPostedEx: number
    feesPendingEx: number
    taxPosted: number
    taxPending: number
    feesPostedDisplay: number
    feesPendingDisplay: number
    reserveHeld: number
    nextReserveRelease: string | null
    netPosted: number
    paidOut: number
    scheduledPayoutTotal: number
  }

  // Payout story
  payoutBuckets: PayoutBucket[]
  reserveBucket: ReserveBucket | null

  // Timeline
  timelineItems: TimelineItem[]
}

// Compute net impact for a single ledger row
function computeNetImpact(row: LedgerRow): number {
  if (row.type === 'fee') {
    const ex = row.feeExTax ?? 0
    const tax = row.taxAmount ?? 0
    return ex + tax
  }
  if (
    row.type === 'payment' ||
    row.type === 'refund' ||
    row.type === 'dispute' ||
    row.type === 'dispute_reversal'
  ) {
    return row.gross ?? 0
  }
  if (row.type === 'reserve_hold') {
    // Reserve holds are negative impact
    return -Math.abs(row.settlement?.amountInBucket ?? 0)
  }
  if (row.type === 'reserve_release') {
    // Reserve releases are positive impact
    return Math.abs(row.settlement?.amountInBucket ?? 0)
  }
  return 0
}

// Export for use in components
export function getRowNetImpact(row: LedgerRow): number {
  return computeNetImpact(row)
}

// Check if a row is customer-facing (affects gross impact)
function isCustomerFacing(row: LedgerRow): boolean {
  return (
    row.type === 'payment' ||
    row.type === 'refund' ||
    row.type === 'dispute' ||
    row.type === 'dispute_reversal'
  )
}

// Derive header state from ledger
function deriveHeaderState(ledgerRows: LedgerRow[]): string {
  const posted = ledgerRows.filter((r) => r.status === 'Posted')

  // Check for disputes
  const hasPostedDisputeDebit = posted.some((r) => r.type === 'dispute')
  const hasPostedDisputeReversal = posted.some(
    (r) => r.type === 'dispute_reversal',
  )

  if (hasPostedDisputeDebit && !hasPostedDisputeReversal) {
    return 'Disputed'
  }

  if (hasPostedDisputeReversal) {
    return 'Dispute resolved'
  }

  // Check for refunds
  const paymentCaptured = posted
    .filter((r) => r.type === 'payment')
    .reduce((sum, r) => sum + (r.gross ?? 0), 0)

  const refunds = posted
    .filter((r) => r.type === 'refund')
    .reduce((sum, r) => sum + Math.abs(r.gross ?? 0), 0)

  if (refunds > 0) {
    if (Math.abs(refunds - paymentCaptured) < 0.01) {
      return 'Refunded'
    }
    return 'Partially refunded'
  }

  return 'Completed'
}

// Main computation function
export function computeTransactionViewModel(
  transaction: Transaction,
  ledgerRows: LedgerRow[],
  taxDisplay: 'included' | 'separate' = 'included',
): TransactionViewModel {
  const posted = ledgerRows.filter((r) => r.status === 'Posted')
  const pending = ledgerRows.filter((r) => r.status !== 'Posted')

  // Header
  const headerAmount = posted
    .filter((r) => r.type === 'payment')
    .reduce((sum, r) => sum + (r.gross ?? 0), 0)

  const headerState = deriveHeaderState(ledgerRows)

  // Gross impact (customer-facing only)
  const grossImpact = posted
    .filter(isCustomerFacing)
    .reduce((sum, r) => sum + (r.gross ?? 0), 0)

  // Fees
  const feesPostedEx = posted
    .filter((r) => r.type === 'fee')
    .reduce((sum, r) => sum + (r.feeExTax ?? 0), 0)

  const feesPendingEx = pending
    .filter((r) => r.type === 'fee')
    .reduce((sum, r) => sum + (r.feeExTax ?? 0), 0)

  const taxPosted = posted
    .filter((r) => r.type === 'fee')
    .reduce((sum, r) => sum + (r.taxAmount ?? 0), 0)

  const taxPending = pending
    .filter((r) => r.type === 'fee')
    .reduce((sum, r) => sum + (r.taxAmount ?? 0), 0)

  const feesPostedDisplay =
    taxDisplay === 'included' ? feesPostedEx + taxPosted : feesPostedEx

  const feesPendingDisplay =
    taxDisplay === 'included' ? feesPendingEx + taxPending : feesPendingEx

  // Reserve
  const reserveNetPosted = posted
    .filter((r) => r.type === 'reserve_hold' || r.type === 'reserve_release')
    .reduce((sum, r) => sum + computeNetImpact(r), 0)

  const reserveHeld = Math.max(0, -reserveNetPosted)

  const nextReserveRelease =
    ledgerRows
      .filter((r) => r.type === 'reserve_release')
      .map((r) => r.effectiveAt)
      .sort()[0] || null

  // Net impact (posted only)
  const netPosted = posted.reduce((sum, r) => sum + computeNetImpact(r), 0)

  // Payout buckets
  const payoutMap = new Map<string, PayoutBucket>()

  ledgerRows.forEach((r) => {
    if (!r.settlement || r.settlement.kind !== 'payout') return

    const key = r.settlement.id
    if (!payoutMap.has(key)) {
      payoutMap.set(key, {
        id: r.settlement.id,
        date: r.settlement.date || '',
        status: r.settlement.status,
        total: 0,
        rows: [],
      })
    }

    const bucket = payoutMap.get(key)!
    bucket.total += computeNetImpact(r)
    bucket.rows.push(r.id)
  })

  const payoutBuckets = Array.from(payoutMap.values()).sort((a, b) =>
    a.date.localeCompare(b.date),
  )

  // Reserve bucket
  let reserveBucket: ReserveBucket | null = null
  if (reserveHeld > 0.0001) {
    const reserveRow = ledgerRows.find((r) => r.type === 'reserve_hold')
    if (reserveRow?.settlement?.kind === 'reserve') {
      reserveBucket = {
        id: reserveRow.settlement.id,
        heldNow: reserveHeld,
        releaseAt: nextReserveRelease,
      }
    }
  }

  // Cash movement
  const paidOut = 0 // In this demo, nothing has been paid out yet
  const scheduledPayoutTotal = payoutBuckets.reduce(
    (sum, p) => sum + p.total,
    0,
  )

  // Timeline items (derived from ledger)
  const timelineItems: TimelineItem[] = []

  // Track if we've added network fees to avoid duplicates
  let networkFeesAdded = false

  ledgerRows.forEach((r) => {
    if (r.type === 'payment') {
      timelineItems.push({
        at: r.effectiveAt,
        label: 'Payment captured',
        amount: r.gross,
        type: 'positive',
      })
    } else if (r.type === 'reserve_hold') {
      timelineItems.push({
        at: r.effectiveAt,
        label: 'Reserve hold applied',
        amount: Math.abs(computeNetImpact(r)),
        type: 'neutral',
      })
    } else if (r.type === 'refund') {
      timelineItems.push({
        at: r.effectiveAt,
        label:
          r.gross && Math.abs(r.gross) > 0
            ? 'Partial refund processed'
            : 'Refund processed',
        amount: Math.abs(r.gross ?? 0),
        type: 'negative',
      })
    } else if (
      r.type === 'fee' &&
      (r.posting.toLowerCase().includes('interchange') ||
        r.posting.toLowerCase().includes('scheme'))
    ) {
      // Group interchange and scheme fees as "network fees"
      if (!networkFeesAdded) {
        const networkFeeRows = ledgerRows.filter(
          (row) =>
            row.type === 'fee' &&
            (row.posting.toLowerCase().includes('interchange') ||
              row.posting.toLowerCase().includes('scheme')),
        )
        const totalNetworkFees = networkFeeRows.reduce(
          (sum, row) => sum + Math.abs(computeNetImpact(row)),
          0,
        )

        timelineItems.push({
          at: r.effectiveAt,
          label: 'Network fees posted',
          amount: totalNetworkFees,
          type: 'negative',
        })
        networkFeesAdded = true
      }
    } else if (r.type === 'dispute') {
      timelineItems.push({
        at: r.effectiveAt,
        label: 'Dispute received',
        amount: Math.abs(r.gross ?? 0),
        type: 'negative',
      })
    } else if (r.type === 'dispute_reversal') {
      // Add challenge event before reversal (if not already added)
      const challengeExists = timelineItems.some(
        (t) => t.label === 'Dispute challenged with evidence',
      )
      if (!challengeExists) {
        // Parse the date and subtract some time for the challenge
        const reversalDate = r.effectiveAt
        const challengeDate = reversalDate.replace(/\d{2}:\d{2}$/, '10:00') // Assume challenge was at 10:00

        timelineItems.push({
          at: challengeDate,
          label: 'Dispute challenged with evidence',
          amount: null,
          type: 'neutral',
        })
      }

      timelineItems.push({
        at: r.effectiveAt,
        label: 'Dispute won - funds returned',
        amount: r.gross ?? 0,
        type: 'positive',
      })
    }
  })

  // Sort timeline by date
  timelineItems.sort((a, b) => {
    // Simple string comparison should work for DD/MM/YYYY HH:MM format
    return a.at.localeCompare(b.at)
  })

  return {
    headerAmount,
    headerState,
    summary: {
      grossImpact,
      feesPostedEx,
      feesPendingEx,
      taxPosted,
      taxPending,
      feesPostedDisplay,
      feesPendingDisplay,
      reserveHeld,
      nextReserveRelease,
      netPosted,
      paidOut,
      scheduledPayoutTotal,
    },
    payoutBuckets,
    reserveBucket,
    timelineItems,
  }
}

// Assertion function to validate consistency
export function assertConsistency(
  viewModel: TransactionViewModel,
  transaction: Transaction,
  ledgerRows: LedgerRow[],
): void {
  try {
    const posted = ledgerRows.filter((r) => r.status === 'Posted')
    const tolerance = 0.01

    // Check 1: Net impact matches sum of posted ledger rows
    const sumPostedNetImpact = posted.reduce(
      (sum, r) => sum + computeNetImpact(r),
      0,
    )
    const netDiff = Math.abs(viewModel.summary.netPosted - sumPostedNetImpact)
    if (netDiff > tolerance) {
      console.error(
        `❌ Net impact mismatch: viewModel=${viewModel.summary.netPosted}, ledger=${sumPostedNetImpact}, diff=${netDiff}`,
      )
    } else {
      console.log(`✅ Net impact consistent: ${viewModel.summary.netPosted}`)
    }

    // Check 2: Gross impact matches customer-facing posted rows
    const sumCustomerFacing = posted
      .filter(isCustomerFacing)
      .reduce((sum, r) => sum + (r.gross ?? 0), 0)
    const grossDiff = Math.abs(
      viewModel.summary.grossImpact - sumCustomerFacing,
    )
    if (grossDiff > tolerance) {
      console.error(
        `❌ Gross impact mismatch: viewModel=${viewModel.summary.grossImpact}, ledger=${sumCustomerFacing}, diff=${grossDiff}`,
      )
    } else {
      console.log(
        `✅ Gross impact consistent: ${viewModel.summary.grossImpact}`,
      )
    }

    // Check 3: Header amount matches posted payment captured
    const sumPaymentCaptured = posted
      .filter((r) => r.type === 'payment')
      .reduce((sum, r) => sum + (r.gross ?? 0), 0)
    const headerDiff = Math.abs(viewModel.headerAmount - sumPaymentCaptured)
    if (headerDiff > tolerance) {
      console.error(
        `❌ Header amount mismatch: viewModel=${viewModel.headerAmount}, ledger=${sumPaymentCaptured}, diff=${headerDiff}`,
      )
    } else {
      console.log(`✅ Header amount consistent: ${viewModel.headerAmount}`)
    }

    // Check 4: Scheduled payouts match sum of payout bucket totals
    const sumPayoutBuckets = viewModel.payoutBuckets.reduce(
      (sum, p) => sum + p.total,
      0,
    )
    const payoutDiff = Math.abs(
      viewModel.summary.scheduledPayoutTotal - sumPayoutBuckets,
    )
    if (payoutDiff > tolerance) {
      console.error(
        `❌ Scheduled payouts mismatch: viewModel=${viewModel.summary.scheduledPayoutTotal}, buckets=${sumPayoutBuckets}, diff=${payoutDiff}`,
      )
    } else {
      console.log(
        `✅ Scheduled payouts consistent: ${viewModel.summary.scheduledPayoutTotal}`,
      )
    }

    // Check 5: Reserve held matches posted reserve rows
    const sumReservePosted = posted
      .filter((r) => r.type === 'reserve_hold' || r.type === 'reserve_release')
      .reduce((sum, r) => sum + computeNetImpact(r), 0)
    const reserveHeldFromLedger = Math.max(0, -sumReservePosted)
    const reserveDiff = Math.abs(
      viewModel.summary.reserveHeld - reserveHeldFromLedger,
    )
    if (reserveDiff > tolerance) {
      console.error(
        `❌ Reserve held mismatch: viewModel=${viewModel.summary.reserveHeld}, ledger=${reserveHeldFromLedger}, diff=${reserveDiff}`,
      )
    } else {
      console.log(
        `✅ Reserve held consistent: ${viewModel.summary.reserveHeld}`,
      )
    }

    console.log('---')
    console.log(
      `Transaction ${transaction.transactionId} consistency check complete`,
    )
  } catch (error) {
    console.error('❌ Consistency check failed with error:', error)
  }
}
